class SplitCreativeGrid extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();

    this.initVideos();
    this.syncTransparentHeaderColor();

    window.addEventListener('scroll', () => this.syncTransparentHeaderColor(), {
      signal: this.abortController.signal,
      passive: true,
    });
    window.addEventListener('resize', () => this.syncTransparentHeaderColor(), {
      signal: this.abortController.signal,
    });
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  syncTransparentHeaderColor() {
    if (!this.classList.contains('header-is-transparent')) {
      return;
    }

    const headerColor = this.dataset.transparentHeaderColor;
    if (!headerColor) {
      return;
    }

    document.documentElement.style.setProperty('--transparent-header-menu-text-color', headerColor);
  }

  initVideos() {
    this.querySelectorAll('.split-creative-grid__media--local-video').forEach((media) => {
      this.initVideo(media);
    });
  }

  initVideo(media) {
    const video = media.querySelector('video');
    const controls = media.querySelector('[data-split-controls]');

    if (!video || video.dataset.splitCreativeGridReady === 'true') {
      return;
    }

    video.dataset.splitCreativeGridReady = 'true';
    video.muted = true;
    media.dataset.paused = video.paused ? 'true' : 'false';
    media.dataset.muted = video.muted ? 'true' : 'false';

    const playButton = controls?.querySelector('[data-split-play]');
    const progress = controls?.querySelector('[data-split-progress]');
    const seek = controls?.querySelector('[data-split-seek]');
    const forwardButton = controls?.querySelector('[data-split-forward]');
    const fullscreenButton = controls?.querySelector('[data-split-fullscreen]');
    const muteButton = controls?.querySelector('[data-split-mute]');
    const signal = this.abortController.signal;

    const stopControlEvent = (event) => {
      event.preventDefault();
      event.stopPropagation();
    };

    const syncState = () => {
      media.dataset.paused = video.paused ? 'true' : 'false';
      media.dataset.muted = video.muted ? 'true' : 'false';
      playButton?.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
      muteButton?.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
    };

    const updateProgress = () => {
      if (!Number.isFinite(video.duration) || video.duration <= 0) {
        return;
      }

      const value = Math.min((video.currentTime / video.duration) * 100, 100);
      if (progress) {
        progress.value = value;
      }
      if (seek && document.activeElement !== seek) {
        seek.value = value;
      }
    };

    const playVideo = () => {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          syncState();
        });
      }
    };

    video.addEventListener('play', syncState, { signal });
    video.addEventListener('pause', syncState, { signal });
    video.addEventListener('volumechange', syncState, { signal });
    video.addEventListener('loadedmetadata', updateProgress, { signal });
    video.addEventListener('timeupdate', updateProgress, { signal });

    if (controls) {
      controls.addEventListener('click', (event) => event.stopPropagation(), { signal });
      controls.addEventListener('pointerdown', (event) => event.stopPropagation(), { signal });
    }

    playButton?.addEventListener('click', (event) => {
      stopControlEvent(event);

      if (video.paused) {
        playVideo();
      } else {
        video.pause();
      }
    }, { signal });

    seek?.addEventListener('input', (event) => {
      stopControlEvent(event);

      if (!Number.isFinite(video.duration) || video.duration <= 0) {
        return;
      }

      video.currentTime = (Number(event.currentTarget.value) / 100) * video.duration;
      updateProgress();
    }, { signal });

    forwardButton?.addEventListener('click', (event) => {
      stopControlEvent(event);

      if (!Number.isFinite(video.duration) || video.duration <= 0) {
        return;
      }

      video.currentTime = Math.min(video.currentTime + 10, video.duration);
      updateProgress();
    }, { signal });

    fullscreenButton?.addEventListener('click', (event) => {
      stopControlEvent(event);

      const fullscreenTarget = media.closest('.split-creative-grid__tile') || media;

      if (document.fullscreenElement) {
        document.exitFullscreen?.();
        return;
      }

      if (fullscreenTarget.requestFullscreen) {
        fullscreenTarget.requestFullscreen();
      } else if (video.webkitEnterFullscreen) {
        video.webkitEnterFullscreen();
      }
    }, { signal });

    muteButton?.addEventListener('click', (event) => {
      stopControlEvent(event);
      video.muted = !video.muted;
      syncState();
    }, { signal });

    syncState();
    updateProgress();

    if (video.autoplay && video.paused) {
      playVideo();
    }
  }
}

if (!customElements.get('split-creative-grid')) {
  customElements.define('split-creative-grid', SplitCreativeGrid);
}
